import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, ArrowRight } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (e) {
        console.error("Error fetching blog post:", e);
        setError(language === 'de' ? "Beitrag nicht gefunden" : "Post not found");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug, language]);

  const getTitle = () => {
    if (!post) return '';
    return language === 'en' && post.title_en ? post.title_en : post.title;
  };

  const getContent = () => {
    if (!post) return '';
    return language === 'en' && post.content_en ? post.content_en : post.content;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="page-header py-20 text-white">
          <div className="container mx-auto px-4">
            <div className="h-8 bg-slate-700 w-48 animate-pulse mb-4"></div>
            <div className="h-12 bg-slate-700 w-3/4 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white" data-testid="blog-post-error">
        <div className="page-header py-20 text-white">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl font-bold">
              {language === 'de' ? 'Beitrag nicht gefunden' : 'Post Not Found'}
            </h1>
          </div>
        </div>
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-slate-600 mb-8">
            {language === 'de' 
              ? 'Der angeforderte Beitrag konnte leider nicht gefunden werden.'
              : 'The requested post could not be found.'}
          </p>
          <Button 
            onClick={() => navigate("/blog")}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
          </Button>
        </div>
      </div>
    );
  }

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-bold text-slate-900 mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-bold text-slate-900 mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      }
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="text-slate-600 leading-relaxed ml-4 mb-2">
            {line.replace('- ', '')}
          </li>
        );
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return (
        <p key={index} className="text-slate-600 leading-relaxed mb-4">
          {line}
        </p>
      );
    });
  };

  return (
    <div data-testid="blog-post-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-slate-300 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" aria-hidden="true" />
            {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {getTitle()}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar size={18} aria-hidden="true" />
              <span>
                {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User size={18} aria-hidden="true" />
              <span>{post.author}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Post Content */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {post.image_url && (
              <img
                src={post.image_url}
                alt={getTitle()}
                className="w-full mb-10 shadow-lg"
              />
            )}
            
            <div className="prose prose-lg max-w-none">
              {renderContent(getContent())}
            </div>
            
            {/* CTA Box */}
            <div className="mt-16 bg-slate-50 p-8 md:p-10 border-l-4 border-orange-600">
              <h3 className="font-bold text-2xl text-slate-900 mb-4">
                {language === 'de' ? 'Haben Sie Fragen?' : 'Do You Have Questions?'}
              </h3>
              <p className="text-slate-600 mb-6">
                {language === 'de' 
                  ? 'Kontaktieren Sie uns gerne für ein unverbindliches Beratungsgespräch zu Ihren Logistikanforderungen.'
                  : 'Feel free to contact us for a non-binding consultation about your logistics requirements.'}
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold"
                  data-testid="blog-post-cta"
                >
                  {t('common.contactUs')}
                  <ArrowRight className="ml-2" size={16} aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
